import { useTheme } from '@neuroforge/ui'
import { useEffect, useRef } from 'react'

import './VantaBackground.css'

interface VantaEffectInstance {
  destroy: () => void
}

// Palette du fog par thème (couleurs three = entiers hex 0xRRGGBB).
// Options réelles de l'effet FOG : highlight / midtone / lowlight forment le
// dégradé des nuages ; base + background = la couleur de fond (alignée sur --bg).
const FOG_THEME = {
  dark: {
    highlightColor: 0x6366f1, // indigo
    midtoneColor: 0x7c3aed, // violet
    lowlightColor: 0x22d3ee, // cyan
    baseColor: 0x07080d,
    backgroundColor: 0x07080d,
  },
  light: {
    highlightColor: 0x818cf8,
    midtoneColor: 0xa78bfa,
    lowlightColor: 0x67e8f9,
    baseColor: 0xf8fafc,
    backgroundColor: 0xf8fafc,
  },
} as const

/**
 * Décor hero du Hub — fog WebGL Vanta (three.js) en fond plein écran.
 *
 * three + vanta sont chargés en dynamique pour ne pas alourdir le bundle initial.
 * Le fog se ré-initialise au changement de thème (couleurs adaptées) et est
 * désactivé sous `prefers-reduced-motion` (le fond uni du body prend le relais).
 */
export default function VantaBackground() {
  const ref = useRef<HTMLDivElement>(null)
  const { isDark } = useTheme()

  useEffect(() => {
    const prefersReduced =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

    if (prefersReduced) return

    let effect: VantaEffectInstance | null = null
    let cancelled = false
    const palette = isDark ? FOG_THEME.dark : FOG_THEME.light

    void (async () => {
      try {
        const THREE = await import('three')
        const { default: FOG } = await import('vanta/dist/vanta.fog.min.js')
        if (cancelled || !ref.current) return

        effect = FOG({
          el: ref.current,
          THREE,
          mouseControls: true,
          touchControls: true,
          gyroControls: false,
          minHeight: 200,
          minWidth: 200,
          highlightColor: palette.highlightColor,
          midtoneColor: palette.midtoneColor,
          lowlightColor: palette.lowlightColor,
          baseColor: palette.baseColor,
          backgroundColor: palette.backgroundColor,
          blurFactor: 0.6,
          speed: 1.0,
          zoom: 0.8,
        })

        if (cancelled) {
          effect?.destroy()
          effect = null
        }
      } catch (err) {
        // WebGL indisponible / échec d'init → le fond uni du body reste affiché.
        console.error('[VantaBackground] fog init failed', err)
      }
    })()

    return () => {
      cancelled = true
      effect?.destroy()
      effect = null
    }
  }, [isDark])

  return <div ref={ref} className="vanta-fog" aria-hidden="true" />
}
